import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, X, ArrowRightLeft } from "lucide-react";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const DISCIPLINAS = ["Dibujo", "Pintura", "Cerámica", "Art&Wine", "Curso de verano", "Otro"];

const empty = {
  nombre: "", disciplina: "Dibujo", sede_id: "", profesor_id: "",
  horarios: [], capacidad: 12, rango_edad: "", precio_mensual: 0,
  fecha_inicio: "", fecha_fin: "", activa: true,
};

export default function Clases() {
  const [items, setItems] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [profes, setProfes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [h, setH] = useState({ dia: "Lunes", hora_inicio: "17:00", hora_fin: "18:30", capacidad: 12 });
  const [mover, setMover] = useState(null);
  const [errorCarga, setErrorCarga] = useState("");

  const load = () => api.get("/clases")
    .then(({ data }) => { setItems(data); setErrorCarga(""); })
    .catch((e) => setErrorCarga(formatApiErrorDetail(e.response?.data?.detail)
      || "No se han podido cargar las clases."));
  useEffect(() => {
    load();
    api.get("/sedes").then(({ data }) => setSedes(data));
    api.get("/profesores").then(({ data }) => setProfes(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.sede_id) return toast.error("Selecciona una sede");
    try {
      const payload = {
        ...form,
        capacidad: Number(form.capacidad) || 0,
        precio_mensual: Number(form.precio_mensual) || 0,
        profesor_id: form.profesor_id || null,
      };
      if (editId) await api.put(`/clases/${editId}`, payload);
      else await api.post("/clases", payload);
      toast.success(editId ? "Clase actualizada" : "Clase creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(
      "La clase se retirará del catálogo pero se conserva para no perder el histórico.\n\n"
      + "Sus clases futuras sin lista pasada se borran; las que ya tienen asistencia se mantienen. ¿Continuamos?"
    )) return;
    const { data } = await api.delete(`/clases/${id}`);
    toast.success(`Clase retirada · ${data.sesiones_futuras_retiradas ?? 0} clase(s) futuras quitadas`
      + ((data.sesiones_conservadas ?? 0) > 0 ? ` · ${data.sesiones_conservadas} con histórico conservadas` : ""));
    load();
  };

  const sedeName = (id) => sedes.find((s) => s.id === id)?.nombre || "—";
  const profeName = (id) => {
    const p = profes.find((x) => x.id === id);
    return p ? `${p.nombre} ${p.apellidos}` : "Sin asignar";
  };

  return (
    <div data-testid="clases-page">
      <PageHeader
        title="Clases y horarios"
        subtitle="Configura la oferta formativa por sede"
        action={
          <Button className="rounded-full font-semibold transition-transform duration-200 hover:-translate-y-[1px]"
            onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-clase-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva clase
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((c) => (
          <Card key={c.id} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`clase-card-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <Badge variant="outline" className="rounded-full mb-2">{c.disciplina}</Badge>
                <h3 className="font-display font-bold text-lg leading-tight">{c.nombre}</h3>
                <p className="text-xs text-muted-foreground mt-1">{sedeName(c.sede_id)} · {profeName(c.profesor_id)}</p>
              </div>
              <Badge variant={c.activa ? "default" : "secondary"}>{c.activa ? "Activa" : "Inactiva"}</Badge>
            </div>
            <div className="mt-4 space-y-1.5">
              {(c.horarios || []).map((x, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  {x.dia} · {x.hora_inicio} – {x.hora_fin}
                  <span className="text-xs">({Number(x.capacidad) > 0 ? x.capacidad : (Number(c.capacidad) || 12)} plazas)</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-4 text-sm flex-wrap">
              <span className="text-muted-foreground">Plazas: <b className="text-foreground">{c.capacidad}</b></span>
              <span className="text-muted-foreground">{c.precio_mensual} €/mes</span>
              {c.rango_edad && <span className="text-muted-foreground">{c.rango_edad}</span>}
              {(c.fecha_inicio || c.fecha_fin) && (
                <span className="text-muted-foreground" data-testid={`clase-rango-${c.id}`}>
                  {c.fecha_inicio} → {c.fecha_fin}
                </span>
              )}
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" size="sm" className="rounded-lg"
                onClick={() => { setForm({ ...empty, ...c, profesor_id: c.profesor_id || "" }); setEditId(c.id); setOpen(true); }}
                data-testid={`edit-clase-${c.id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg text-destructive"
                onClick={() => remove(c.id)} data-testid={`delete-clase-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg"
                onClick={() => { setMover({ origen: c, destino: "" }); }}
                data-testid={`mover-alumnos-${c.id}`}>
                <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> Mover alumnos
              </Button>
            </div>
          </Card>
        ))}
        {errorCarga ? (
          <Card className="rounded-2xl p-6 border-destructive/40 bg-destructive/5 md:col-span-2 xl:col-span-3"
            data-testid="clases-error">
            <p className="font-semibold text-destructive">No se han podido cargar las clases</p>
            <p className="text-sm text-muted-foreground mt-1">{errorCarga}</p>
            <Button variant="outline" className="rounded-full mt-4" onClick={load}
              data-testid="clases-reintentar-btn">
              Reintentar
            </Button>
          </Card>
        ) : items.length === 0 && (
          <p className="text-muted-foreground text-sm" data-testid="clases-vacio">Todavía no hay clases.</p>
        )}
      </div>

      <Dialog open={!!mover} onOpenChange={(v) => !v && setMover(null)}>
        <DialogContent className="rounded-2xl" data-testid="mover-alumnos-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">
            Mover alumnos de clase
          </DialogTitle>
          <DialogDescription>
            Traslada las matrículas a otra clase de la misma sede y desactiva la actual.
          </DialogDescription></DialogHeader>
          <div className="grid gap-4">
            <p className="text-sm text-muted-foreground">
              Los alumnos de <strong>{mover?.origen?.nombre}</strong> pasarán a la clase que elijas
              y la de origen se desactivará (se retiran sus clases futuras sin lista pasada).
            </p>
            <div className="grid gap-2">
              <Label>Clase de destino</Label>
              <Select value={mover?.destino || ""} onValueChange={(v) => setMover((m) => ({ ...m, destino: v }))}>
                <SelectTrigger data-testid="mover-destino-select"><SelectValue placeholder="Elige la clase" /></SelectTrigger>
                <SelectContent className="max-h-[50vh]">
                  {items.filter((c) => c.id !== mover?.origen?.id && c.sede_id === mover?.origen?.sede_id)
                    .map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" disabled={!mover?.destino}
              data-testid="mover-confirmar-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/clases/${mover.origen.id}/mover-alumnos`,
                    { destino_clase_id: mover.destino, desactivar_origen: true });
                  toast.success(`${data.movidos} alumno(s) movidos · clase de origen desactivada`);
                  setMover(null);
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
                }
              }}>
              Mover y desactivar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="clase-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Editar clase" : "Nueva clase"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Nombre de la clase</Label>
              <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="clase-nombre-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Disciplina</Label>
                <Select value={form.disciplina} onValueChange={(v) => set("disciplina", v)}>
                  <SelectTrigger data-testid="clase-disciplina-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISCIPLINAS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Sede</Label>
                <Select value={form.sede_id} onValueChange={(v) => set("sede_id", v)}>
                  <SelectTrigger data-testid="clase-sede-select"><SelectValue placeholder="Selecciona sede" /></SelectTrigger>
                  <SelectContent>
                    {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Profesor</Label>
              <Select value={form.profesor_id} onValueChange={(v) => set("profesor_id", v)}>
                <SelectTrigger data-testid="clase-profesor-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  {profes.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre} {p.apellidos}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Plazas por defecto</Label>
                <Input type="number" value={form.capacidad} onChange={(e) => set("capacidad", e.target.value)} data-testid="clase-capacidad-input" />
              </div>
              <div className="grid gap-2">
                <Label>Precio /mes</Label>
                <Input type="number" value={form.precio_mensual} onChange={(e) => set("precio_mensual", e.target.value)} data-testid="clase-precio-input" />
              </div>
              <div className="grid gap-2">
                <Label>Edad</Label>
                <Input value={form.rango_edad} onChange={(e) => set("rango_edad", e.target.value)} placeholder="6-12 años" data-testid="clase-edad-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Inicio del curso (opcional)</Label>
                <Input type="date" value={form.fecha_inicio || ""} onChange={(e) => set("fecha_inicio", e.target.value)} data-testid="clase-inicio-input" />
              </div>
              <div className="grid gap-2">
                <Label>Fin del curso (opcional)</Label>
                <Input type="date" value={form.fecha_fin || ""} onChange={(e) => set("fecha_fin", e.target.value)} data-testid="clase-fin-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Úsalo para cursos de verano o intensivos: solo se generarán sesiones dentro de ese rango.
            </p>

            <div className="rounded-lg border border-border p-4">
              <Label className="mb-3 block">Horarios</Label>
              <div className="space-y-2 mb-4">
                {form.horarios.map((x, i) => (
                  <div key={x.horario_id || i} className="flex items-center justify-between gap-2 bg-muted rounded-lg px-3 py-2 text-sm">
                    <span className="min-w-0 truncate">
                      {x.dia} · {x.hora_inicio} – {x.hora_fin} ·{" "}
                      {Number(x.capacidad) > 0 ? x.capacidad : (Number(form.capacidad) || 12)} plazas
                    </span>
                    <button type="button" className="shrink-0"
                      onClick={() => set("horarios", form.horarios.filter((_, j) => j !== i))}
                      data-testid={`remove-horario-${i}`}>
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2">
                <Select value={h.dia} onValueChange={(v) => setH({ ...h, dia: v })}>
                  <SelectTrigger className="min-w-0 sm:w-36 sm:shrink-0" data-testid="horario-dia-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIAS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="time" className="min-w-0 sm:flex-1" value={h.hora_inicio} onChange={(e) => setH({ ...h, hora_inicio: e.target.value })} data-testid="horario-inicio-input" />
                <Input type="time" className="min-w-0 sm:flex-1" value={h.hora_fin} onChange={(e) => setH({ ...h, hora_fin: e.target.value })} data-testid="horario-fin-input" />
                <Input type="number" min="1" className="min-w-0 sm:w-20 sm:shrink-0" placeholder="Plazas" value={h.capacidad}
                  onChange={(e) => setH({ ...h, capacidad: e.target.value })} data-testid="horario-capacidad-input" />
                <Button type="button" variant="outline" className="rounded-lg col-span-2 sm:col-span-1 shrink-0"
                  onClick={() => {
                    const plazas = Number(h.capacidad) || 0;
                    if (plazas < 1) return toast.error("Indica las plazas de ese horario");
                    set("horarios", [...form.horarios, { ...h, capacidad: plazas }]);
                  }} data-testid="add-horario-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Cada día y hora tiene su propio aforo: así puedes tener 8 plazas el lunes y 12 el miércoles.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label>Clase activa</Label>
              <Switch checked={form.activa} onCheckedChange={(v) => set("activa", v)} data-testid="clase-activa-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={save} data-testid="save-clase-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
